import { Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useIsSuperAdmin } from "@/hooks/useIsSuperAdmin";
import { useHasResolvedOnce } from "@/hooks/useHasResolvedOnce";
import { BrandedLoadingSpinner } from "@/components/brand";

/**
 * Gate for founder-only routes. Standalone by design — it does NOT reuse
 * ProtectedRoute's admin/org logic, which would bounce an agent-role founder to
 * /admin/setup. It requires only (a) an authenticated session and (b) super-admin
 * membership. Redirects everyone else to /feed.
 *
 * This is one of three defense-in-depth layers (nav visibility, this route guard,
 * an in-page re-check); the authoritative check is the edge function.
 */
const SuperAdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading: authLoading } = useAuth();
  const { isSuperAdmin, isLoading: superLoading } = useIsSuperAdmin();

  // Loader only before the first resolution — a later loading state is a
  // background revalidation and must not unmount the subtree. See
  // useHasResolvedOnce; same treatment as ProtectedRoute.
  const isResolving = authLoading || (user && superLoading);
  const hasResolvedOnce = useHasResolvedOnce(!isResolving);

  if (isResolving && !hasResolvedOnce) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <BrandedLoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (!isSuperAdmin) {
    return <Navigate to="/feed" replace />;
  }

  return <>{children}</>;
};

export default SuperAdminRoute;
